import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { useUsers } from '../context/UsersContext';
import { useCheckTypes, useWorkflowStatus, useClarificationTypes } from '../hooks/useMasterData';
import AdvancedFilterDrawer from '../components/AdvancedFilterDrawer';
import MonthYearPicker from '../components/MonthYearPicker';
import SearchableDropdown from '../components/SearchableDropdown';
import ColumnSelector from '../components/ColumnSelector';
import Tooltip from '../components/Tooltip';
import UserTimestamp from '../components/UserTimestamp';
import { formatDateRange, formatDateTime, getMonthRange, getCurrentMonthYear, formatMonthYear, formatDateUS } from '../utils/dateUtils';
import { filterEmojis } from '../utils/emojiFilter';
import { loadVisibleColumns, persistVisibleColumns } from '../utils/columnPreferences';
import { useResizableTableColumns } from '../hooks/useResizableTableColumns';
import { CellEllipsis } from '../components/CellEllipsis';
import { ResizableTh } from '../components/ResizableTableHeader';
import { Copy, RotateCcw } from 'lucide-react';
import './Clarifications.css';

const CLARIFICATIONS_COLUMN_PREFS_KEY = 'clarificationsTable';
const CLARIFICATIONS_TABLE_WIDTHS_KEY = 'clarificationsTableColWidths';
const CLARIFICATIONS_DEFAULT_COL_WIDTH_PX = {
  checkNumber: 120,
  clarificationId: 132,
  checkType: 104,
  depositDate: 100,
  amount: 96,
  assigneeType: 104,
  assignee: 124,
  reporteeType: 104,
  reporter: 124,
  clarificationType: 132,
  status: 104,
  workflowStatus: 124,
  openedAt: 112,
  resolvedAt: 112,
  pageNumber: 92,
  patientName: 124,
  patientAccountNumber: 132,
  insurance: 108,
  details: 168,
  createdAt: 112,
  updatedAt: 112
};

const Clarifications = () => {
  const { showError, showSuccess } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const { data: checkTypes } = useCheckTypes(true);
  const { data: workflowStatuses } = useWorkflowStatus(true);
  const { data: clarificationTypes } = useClarificationTypes(true);
  
  // State management
  const [clarifications, setClarifications] = useState([]);
  const [allClarifications, setAllClarifications] = useState([]); // Store all fetched clarifications before filtering
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Storage key for persisting filters
  const STORAGE_KEY = 'clarificationsFiltersState';

  // Helper function to load filters from sessionStorage (called once during initialization)
  const loadInitialFilters = () => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.error('Error loading filters from sessionStorage:', error);
    }
    return null;
  };

  const initialFilters = loadInitialFilters();

  // Pagination - initialize from storage if available
  const [currentPage, setCurrentPage] = useState(initialFilters?.currentPage ?? 0);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('');
  
  // Default filters - initialize from storage if available
  const [searchTerm, setSearchTerm] = useState(initialFilters?.searchTerm ?? '');
  const [selectedStatus, setSelectedStatus] = useState(initialFilters?.selectedStatus ?? '');
  const { getUserName, getUserById } = useUsers(); // Get users from context
  const [practices, setPractices] = useState([]);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);

  // Column selector
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const columnSelectorTriggerRef = useRef(null);

  // Hide pagination during filter changes to prevent flickering
  const [showPagination, setShowPagination] = useState(true);
  
  // Available columns
  const allColumns = [
    { key: 'checkNumber', label: 'Check Number' },
    { key: 'clarificationId', label: 'Clarification ID' },
    { key: 'checkType', label: 'Check Type' },
    { key: 'depositDate', label: 'Deposit Date' },
    { key: 'amount', label: 'Amount' },
    { key: 'assigneeType', label: 'Assignee' },
    { key: 'assignee', label: 'Assignee Name' },
    { key: 'reporteeType', label: 'Reporter' },
    { key: 'reporter', label: 'Reporter Name' },
    { key: 'clarificationType', label: 'Clarification Type' },
    { key: 'status', label: 'Status' },
    { key: 'workflowStatus', label: 'Workflow Status' },
    { key: 'openedAt', label: 'Opened Date' },
    { key: 'resolvedAt', label: 'Resolved Date' },
    { key: 'pageNumber', label: 'Page Number' },
    { key: 'patientName', label: 'Patient Name' },
    { key: 'patientAccountNumber', label: 'Patient Account #' },
    { key: 'insurance', label: 'Insurance' },
    { key: 'details', label: 'Details' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' }
  ];

  // Default visible columns (max 10 for ColumnSelector): includes Clarification ID; Updated At available via column picker
  const defaultColumns = [
    'checkNumber',
    'clarificationId',
    'depositDate',
    'amount',
    'assigneeType',
    'reporteeType',
    'clarificationType',
    'status',
    'openedAt',
    'resolvedAt'
  ];

  const [visibleColumns, setVisibleColumns] = useState(() =>
    loadVisibleColumns(
      CLARIFICATIONS_COLUMN_PREFS_KEY,
      new Set(allColumns.map(c => c.key)),
      defaultColumns
    )
  );

  const clarificationsColumnOrder = useMemo(() => [...visibleColumns], [visibleColumns]);
  const { Colgroup, onResizeMouseDown, resetColumnWidths } = useResizableTableColumns({
    storageKey: CLARIFICATIONS_TABLE_WIDTHS_KEY,
    columnIds: clarificationsColumnOrder,
    defaultWidths: CLARIFICATIONS_DEFAULT_COL_WIDTH_PX,
    minWidth: 48
  });
  
  // Advanced filters - now support arrays for multiple selections
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filtersApplyTick, setFiltersApplyTick] = useState(0);
  const defaultAdvancedFilters = {
    statuses: [], // Array for multiple status selections
    excludeStatuses: [],
    practiceCodes: [], // Array for multiple practice selections
    excludePracticeCodes: [],
    locationCodes: [], // Array for multiple location selections
    excludeLocationCodes: [],
    assigneeIds: [], // Array for multiple assignee selections
    excludeAssigneeIds: [],
    reporterIds: [], // Array for multiple reporter selections
    excludeReporterIds: [],
    assigneeTypes: [], // Array for multiple assignee type selections (ONSHORE, OFFSHORE)
    reporteeTypes: [], // Array for multiple reportee type selections (ONSHORE, OFFSHORE)
    checkTypes: [], // Array for multiple check type selections
    excludeCheckTypes: [],
    workflowStatuses: [], // Array for multiple workflow status selections
    excludeWorkflowStatuses: [],
    checkNumber: '',
    clarificationType: '', // Clarification type filter with wildcard support
    excludeClarificationTypes: [],
    unknown: undefined, // undefined = ALL, false = false, true = true
    depositDateFrom: '',
    depositDateTo: '',
    resolvedDateFrom: '',
    resolvedDateTo: '',
    openedDateFrom: '',
    openedDateTo: '',
    startDate: '', // Legacy support
    endDate: '', // Legacy support
    month: null,
    year: null
  };
  const [advancedFilters, setAdvancedFilters] = useState(
    initialFilters?.advancedFilters ?? defaultAdvancedFilters
  );
  
  // Sorting - initialize from storage if available
  const [sortField, setSortField] = useState(initialFilters?.sortField ?? 'openedAt');
  const [sortDirection, setSortDirection] = useState(initialFilters?.sortDirection ?? 'desc'); // Default: newest first

  // Month/Year selection - initialize from storage or defaults
  const currentMonthYear = getCurrentMonthYear();
  const [selectedMonth, setSelectedMonth] = useState(
    initialFilters?.selectedMonth ?? currentMonthYear.month
  );
  const [selectedYear, setSelectedYear] = useState(
    initialFilters?.selectedYear ?? currentMonthYear.year
  );
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  
  const handleMonthYearSelect = (month, year) => {
    setSelectedMonth(month);
    setSelectedYear(year);
    setShowMonthPicker(false);
  };

  // Function to save filters to sessionStorage
  const saveFiltersToStorage = useCallback(() => {
    try {
      const filterState = {
        searchTerm,
        selectedStatus,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filterState));
    } catch (error) {
      console.error('Error saving filters to sessionStorage:', error);
    }
  }, [searchTerm, selectedStatus, advancedFilters, selectedMonth, selectedYear, sortField, sortDirection, currentPage]);

  const openClarificationDetail = useCallback(
    (clarification) => {
      if (!clarification?.checkId) return;
      const completeFilters = {
        searchTerm,
        selectedStatus,
        advancedFilters,
        selectedMonth,
        selectedYear,
        sortField,
        sortDirection,
        currentPage
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(completeFilters));
      saveFiltersToStorage();
      navigate(
        `/checks/${clarification.checkId}?clarificationId=${clarification.clarificationId}&source=clarifications`
      );
    },
    [
      searchTerm,
      selectedStatus,
      advancedFilters,
      selectedMonth,
      selectedYear,
      sortField,
      sortDirection,
      currentPage,
      navigate,
      saveFiltersToStorage
    ]
  );

  const handleCopyCheckNumber = useCallback(
    async (checkNumber) => {
      if (!checkNumber) return;
      try {
        await navigator.clipboard.writeText(checkNumber);
        showSuccess('Check number copied');
      } catch (err) {
        console.error('Copy failed:', err);
        showError('Could not copy check number');
      }
    },
    [showSuccess, showError]
  );

  // Save filters to sessionStorage whenever they change (skip initial mount to avoid overwriting)
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return; // Skip saving on initial mount
    }
    saveFiltersToStorage();
  }, [saveFiltersToStorage]);

  // Restore filters when navigating back to /clarifications page
  const hasRestoredFromStorage = useRef(false);
  const isRestoringFromStorage = useRef(false);
  useEffect(() => {
    if (location.pathname === '/clarifications' && !hasRestoredFromStorage.current) {
      const stored = loadInitialFilters();
      if (stored) {
        isRestoringFromStorage.current = true;
        // Keep pagination visible when restoring from storage
        setShowPagination(true);
        // Restore page first (before filters) to prevent reset effect from interfering
        if (stored.currentPage !== undefined && stored.currentPage !== currentPage) {
          setCurrentPage(stored.currentPage);
        }
        // Restore filters - the reset effect will be skipped due to isRestoringFromStorage flag
        if (stored.searchTerm !== searchTerm) setSearchTerm(stored.searchTerm || '');
        if (stored.selectedStatus !== selectedStatus) setSelectedStatus(stored.selectedStatus || '');
        if (JSON.stringify(stored.advancedFilters) !== JSON.stringify(advancedFilters)) {
          setAdvancedFilters(stored.advancedFilters || defaultAdvancedFilters);
        }
        if (stored.selectedMonth !== selectedMonth) setSelectedMonth(stored.selectedMonth ?? currentMonthYear.month);
        if (stored.selectedYear !== selectedYear) setSelectedYear(stored.selectedYear ?? currentMonthYear.year);
        if (stored.sortField !== sortField) setSortField(stored.sortField || 'openedAt');
        if (stored.sortDirection !== sortDirection) setSortDirection(stored.sortDirection || 'desc');
        // Clear the flag after all restorations are queued
        setTimeout(() => {
          isRestoringFromStorage.current = false;
        }, 0);
      }
      hasRestoredFromStorage.current = true;
    }
    // Reset the flag when navigating away
    if (location.pathname !== '/clarifications') {
      hasRestoredFromStorage.current = false;
      isRestoringFromStorage.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Removed: Users are now loaded from context on login, no need to fetch here

  // Fetch practices on mount
  useEffect(() => {
    fetchPractices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPractices = async () => {
    try {
      const response = await api.getAllPractices();
      const practicesList = Array.isArray(response) ? response : (response?.items || []);
      setPractices(practicesList.filter(p => p.isActive !== false));
    } catch (error) {
      console.error('Error fetching practices:', error);
      const errorMessage = error?.message || error?.data?.message || 'Failed to fetch practices';
      showError(errorMessage);
    }
  };

  const fetchClarifications = useCallback(async (page = 0) => {
    // Set loading state but keep old data visible to prevent flickering
    setLoading(true);
    setError(null);
    
    try {
      // Build search parameters for POST request
      const searchParams = {
        page: page,
        size: 50
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus) {
        statuses.push(selectedStatus);
      }
      if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
        statuses.push(...advancedFilters.statuses);
      }
      // Remove duplicates
      if (statuses.length > 0) {
        searchParams.statuses = [...new Set(statuses)];
      }
      if (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) {
        searchParams.excludeStatuses = [...new Set(advancedFilters.excludeStatuses)];
      }

      // Practice codes (array)
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        searchParams.practiceCodes = [...new Set(advancedFilters.practiceCodes)];
      }
      if (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) {
        searchParams.excludePracticeCodes = [...new Set(advancedFilters.excludePracticeCodes)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        searchParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }
      if (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) {
        searchParams.excludeLocationCodes = [...new Set(advancedFilters.excludeLocationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        searchParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        searchParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reporter IDs (array)
      if (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) {
        searchParams.reporterIds = [...new Set(advancedFilters.reporterIds)];
      }
      if (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) {
        searchParams.excludeReporterIds = [...new Set(advancedFilters.excludeReporterIds)];
      }

      // Assignee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) {
        searchParams.assigneeTypes = [...new Set(advancedFilters.assigneeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reportee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) {
        searchParams.reporteeTypes = [...new Set(advancedFilters.reporteeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Check Types (array)
      if (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) {
        searchParams.checkTypes = [...new Set(advancedFilters.checkTypes)];
      }
      if (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) {
        searchParams.excludeCheckTypes = [...new Set(advancedFilters.excludeCheckTypes)];
      }

      // Workflow Statuses (array)
      if (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) {
        searchParams.workflowStatuses = [...new Set(advancedFilters.workflowStatuses)];
      }
      if (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) {
        searchParams.excludeWorkflowStatuses = [...new Set(advancedFilters.excludeWorkflowStatuses)];
      }

      // Unknown filter
      if (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) {
        searchParams.unknown = advancedFilters.unknown;
      }

      // Check number (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      // Prioritize explicit checkNumber filter over searchTerm
      if (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') {
        searchParams.checkNumber = advancedFilters.checkNumber.trim().toUpperCase();
      } else if (searchTerm && searchTerm.trim() !== '' && !advancedFilters.checkNumber) {
        // Use searchTerm as checkNumber only if no explicit checkNumber filter is set
        searchParams.checkNumber = searchTerm.trim().toUpperCase();
      }

      // Clarification type (supports wildcards)
      // If user enters text without *, it's exact match
      // If user enters text with *, it's regex/wildcard match
      if (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') {
        searchParams.clarificationType = advancedFilters.clarificationType.trim();
      }
      if (advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0) {
        searchParams.excludeClarificationTypes = [...new Set(advancedFilters.excludeClarificationTypes)];
      }

      // Date filters - use openedDateFrom/To, depositDateFrom/To, resolvedDateFrom/To
      // Check if user has set any date filters
      const hasDepositDateFilter = advancedFilters.depositDateFrom || advancedFilters.depositDateTo;
      const hasResolvedDateFilter = advancedFilters.resolvedDateFrom || advancedFilters.resolvedDateTo;
      const hasOpenedDateFilter = advancedFilters.openedDateFrom || advancedFilters.openedDateTo;
      
      // Check if any advanced filters are active
      const isAdvancedFiltersActive = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
        (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
        (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
        (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
        (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) ||
        (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) ||
        (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) ||
        (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) ||
        (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') ||
        (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') ||
        (advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0) ||
        (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
        hasDepositDateFilter ||
        hasResolvedDateFilter ||
        hasOpenedDateFilter
      );
      
      // Get month range for default opened date (only if no advanced filters are active)
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // Deposit Date filters - only add if user explicitly set them
      if (advancedFilters.depositDateFrom && advancedFilters.depositDateFrom.trim() !== '') {
        searchParams.depositDateFrom = advancedFilters.depositDateFrom;
      }
      if (advancedFilters.depositDateTo && advancedFilters.depositDateTo.trim() !== '') {
        searchParams.depositDateTo = advancedFilters.depositDateTo;
      }

      // Resolved Date filters - only add if user explicitly set them
      if (advancedFilters.resolvedDateFrom && advancedFilters.resolvedDateFrom.trim() !== '') {
        searchParams.resolvedDateFrom = advancedFilters.resolvedDateFrom;
      }
      if (advancedFilters.resolvedDateTo && advancedFilters.resolvedDateTo.trim() !== '') {
        searchParams.resolvedDateTo = advancedFilters.resolvedDateTo;
      }

      // Opened Date filters
      // Only add default opened date if no advanced filters are active
      if (hasOpenedDateFilter) {
        if (advancedFilters.openedDateFrom) {
          searchParams.openedDateFrom = advancedFilters.openedDateFrom;
        }
        if (advancedFilters.openedDateTo) {
          searchParams.openedDateTo = advancedFilters.openedDateTo;
        }
      } else if (!isAdvancedFiltersActive) {
        // Only use default month range if NO advanced filters are active
        searchParams.openedDateFrom = monthRange.startDate;
        searchParams.openedDateTo = monthRange.endDate;
      }

      // Legacy date filters mapping
      if (advancedFilters.startDate && !searchParams.openedDateFrom) {
        searchParams.openedDateFrom = advancedFilters.startDate;
      }
      if (advancedFilters.endDate && !searchParams.openedDateTo) {
        searchParams.openedDateTo = advancedFilters.endDate;
      }

      // Use POST endpoint with search
      const response = await api.searchClarificationsDashboard(searchParams);
      
      let clarificationsList = response.items || [];
      const totalPages = response.totalPages || 0;
      const totalElements = response.totalElements || 0;
      
      // Client-side sorting
      if (sortField && clarificationsList.length > 0) {
        clarificationsList = [...clarificationsList].sort((a, b) => {
          let aVal = a[sortField];
          let bVal = b[sortField];
          
          if (sortField === 'openedAt' || sortField === 'resolvedAt' || sortField === 'createdAt' || sortField === 'updatedAt') {
            aVal = aVal ? new Date(aVal) : new Date(0);
            bVal = bVal ? new Date(bVal) : new Date(0);
          } else {
            aVal = aVal || '';
            bVal = bVal || '';
          }
          
          if (sortDirection === 'asc') {
            return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
          } else {
            return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
          }
        });
      }
      
      // Update all state at once and show pagination after data loads
      setAllClarifications(clarificationsList);
      setTotalElements(totalElements);
      setTotalPages(totalPages);
      setHasMore(page < totalPages - 1);
      setCurrentPage(page);
      setPageInputValue((page + 1).toString());
      setLoading(false);
      // Show pagination after data is loaded
      setShowPagination(true);
      
    } catch (err) {
      console.error('Error fetching clarifications:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to load clarifications. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
      setLoading(false);
    }
  }, [selectedStatus, searchTerm, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, showError]);

  // Track previous filter values to detect changes
  const prevFiltersRef = useRef({
    selectedStatus,
    advancedFilters: JSON.stringify(advancedFilters),
    selectedMonth,
    selectedYear
  });

  // Fetch clarifications when page or filters change (excluding searchTerm - handled client-side)
  // Reset to page 0 when filters change, but not when only page changes
  useEffect(() => {
    const filtersChanged = 
      prevFiltersRef.current.selectedStatus !== selectedStatus ||
      prevFiltersRef.current.advancedFilters !== JSON.stringify(advancedFilters) ||
      prevFiltersRef.current.selectedMonth !== selectedMonth ||
      prevFiltersRef.current.selectedYear !== selectedYear;

    if (filtersChanged && !isRestoringFromStorage.current) {
      // Hide pagination immediately when filters change to prevent flickering
      setShowPagination(false);
      // Reset to page 0 when filters change
      if (currentPage !== 0) {
        setCurrentPage(0);
        setPageInputValue('1');
        // Update ref for next comparison
        prevFiltersRef.current = {
          selectedStatus,
          advancedFilters: JSON.stringify(advancedFilters),
          selectedMonth,
          selectedYear
        };
        return; // Don't fetch yet, let the page reset trigger the fetch
      }
    }

    // Update ref for next comparison
    prevFiltersRef.current = {
      selectedStatus,
      advancedFilters: JSON.stringify(advancedFilters),
      selectedMonth,
      selectedYear
    };

    // Fetch data
    fetchClarifications(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, selectedStatus, advancedFilters, sortField, sortDirection, selectedMonth, selectedYear, filtersApplyTick]);

  const handlePreviousPage = () => {
    if (currentPage > 0 && !loading) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (hasMore && !loading) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePageInputChange = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setPageInputValue(value);
    }
  };

  const handlePageInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleGoToPage();
    }
  };

  const handleGoToPage = () => {
    if (pageInputValue === '' || loading) return;
    
    const pageNum = parseInt(pageInputValue, 10);
    const maxPage = totalPages > 0 ? totalPages - 1 : 0;
    
    // Validate page number (convert to 0-based index)
    if (pageNum >= 1 && pageNum <= maxPage + 1) {
      setCurrentPage(pageNum - 1);
    } else {
      // Reset to current page if invalid
      setPageInputValue((currentPage + 1).toString());
    }
  };

  // Update page input when currentPage changes externally
  useEffect(() => {
    setPageInputValue((currentPage + 1).toString());
  }, [currentPage]);

  // Calculate pagination display values
  const pageSize = 50;
  // If search is active, show filtered results count, otherwise show total from API
  const displayTotal = searchTerm ? clarifications.length : totalElements;
  const startIndex = searchTerm ? 1 : (currentPage * pageSize + 1);
  const endIndex = searchTerm ? clarifications.length : Math.min((currentPage + 1) * pageSize, totalElements);

  // Client-side filtering for search term
  useEffect(() => {
    if (!searchTerm) {
      setClarifications(allClarifications);
      return;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    const filtered = allClarifications.filter(clarification => {
      // Search by check number
      if (clarification.checkNumber && clarification.checkNumber.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by clarification type
      if (clarification.clarificationType && clarification.clarificationType.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      // Search by details
      if (clarification.details && clarification.details.toLowerCase().includes(searchLower)) {
        return true;
      }

      if (clarification.clarificationId && clarification.clarificationId.toLowerCase().includes(searchLower)) {
        return true;
      }
      
      return false;
    });

    setClarifications(filtered);
  }, [searchTerm, allClarifications]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleApplyAdvancedFilters = (filters) => {
    setAdvancedFilters(filters);
    setFiltersApplyTick(prev => prev + 1);
  };

  const handleResetAdvancedFilters = () => {
    setAdvancedFilters({
      statuses: [],
      excludeStatuses: [],
      practiceCodes: [],
      excludePracticeCodes: [],
      locationCodes: [],
      excludeLocationCodes: [],
      assigneeIds: [],
      excludeAssigneeIds: [],
      reporterIds: [],
      excludeReporterIds: [],
      assigneeTypes: [],
      reporteeTypes: [],
      checkTypes: [],
      excludeCheckTypes: [],
      workflowStatuses: [],
      excludeWorkflowStatuses: [],
      checkNumber: '',
      clarificationType: '',
      excludeClarificationTypes: [],
      unknown: undefined,
      depositDateFrom: '',
      depositDateTo: '',
      resolvedDateFrom: '',
      resolvedDateTo: '',
      openedDateFrom: '',
      openedDateTo: '',
      startDate: '',
      endDate: '',
      month: null,
      year: null
    });
  };

  const handleClearAllFilters = () => {
    handleResetAdvancedFilters();
    setSelectedStatus('');
    setSearchTerm('');
    setShowAdvancedFilters(false);
    sessionStorage.removeItem(STORAGE_KEY);
  };

  // Check if any advanced filter is active
  const hasAdvancedFilters = () => {
    return (
      (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
      (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) ||
      (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
      (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) ||
      (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
      (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) ||
      (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
      (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
      (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
      (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
      (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
      (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
      (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) ||
      (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) ||
      (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) ||
      (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) ||
      (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') ||
      (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') ||
      (advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0) ||
      (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
      (advancedFilters.depositDateFrom && advancedFilters.depositDateFrom.trim() !== '') ||
      (advancedFilters.depositDateTo && advancedFilters.depositDateTo.trim() !== '') ||
      (advancedFilters.resolvedDateFrom && advancedFilters.resolvedDateFrom.trim() !== '') ||
      (advancedFilters.resolvedDateTo && advancedFilters.resolvedDateTo.trim() !== '') ||
      (advancedFilters.openedDateFrom && advancedFilters.openedDateFrom.trim() !== '') ||
      (advancedFilters.openedDateTo && advancedFilters.openedDateTo.trim() !== '') ||
      (advancedFilters.startDate && advancedFilters.startDate.trim() !== '') ||
      (advancedFilters.endDate && advancedFilters.endDate.trim() !== '') ||
      advancedFilters.month ||
      advancedFilters.year
    );
  };

  const getStatusClass = (status) => {
    const statusMap = {
      'RESOLVED': 'status-resolved',
      'OPEN': 'status-open'
    };
    return statusMap[status] || 'status-open';
  };

  const formatStatus = (status) => {
    const statusMap = {
      'OPEN': 'Open',
      'RESOLVED': 'Resolved'
    };
    return statusMap[status] || status || 'Open';
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // getUserName is now provided by useUsers hook from context

  const getDateRangeDisplay = () => {
    // If both start and end dates are selected, show the date range
    if (advancedFilters.startDate && advancedFilters.endDate) {
      return formatDateRange(advancedFilters.startDate, advancedFilters.endDate);
    }
    // If advanced filters are active (but no date range), show custom range
    if (hasAdvancedFilters()) {
      return 'Custom Range';
    }
    // Show month/year format instead of date range
    return formatMonthYear(selectedMonth, selectedYear);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Build export parameters using the same logic as fetchClarifications
      const exportParams = {
        page: 0,
        size: 200
      };

      // Collect statuses from both default and advanced filters
      const statuses = [];
      if (selectedStatus) {
        statuses.push(selectedStatus);
      }
      if (advancedFilters.statuses && advancedFilters.statuses.length > 0) {
        statuses.push(...advancedFilters.statuses);
      }
      // Remove duplicates
      if (statuses.length > 0) {
        exportParams.statuses = [...new Set(statuses)];
      }
      if (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) {
        exportParams.excludeStatuses = [...new Set(advancedFilters.excludeStatuses)];
      }

      // Practice codes (array)
      if (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) {
        exportParams.practiceCodes = [...new Set(advancedFilters.practiceCodes)];
      }
      if (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) {
        exportParams.excludePracticeCodes = [...new Set(advancedFilters.excludePracticeCodes)];
      }

      // Location codes (array)
      if (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) {
        exportParams.locationCodes = [...new Set(advancedFilters.locationCodes)];
      }
      if (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) {
        exportParams.excludeLocationCodes = [...new Set(advancedFilters.excludeLocationCodes)];
      }

      // Assignee IDs (array)
      if (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) {
        exportParams.assigneeIds = [...new Set(advancedFilters.assigneeIds)];
      }
      if (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) {
        exportParams.excludeAssigneeIds = [...new Set(advancedFilters.excludeAssigneeIds)];
      }

      // Reporter IDs (array)
      if (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) {
        exportParams.reporterIds = [...new Set(advancedFilters.reporterIds)];
      }
      if (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) {
        exportParams.excludeReporterIds = [...new Set(advancedFilters.excludeReporterIds)];
      }

      // Assignee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) {
        exportParams.assigneeTypes = [...new Set(advancedFilters.assigneeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Reportee Types (array) - Transform ONSHORE/OFFSHORE to ON-SHORE/OFF-SHORE for API
      if (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) {
        exportParams.reporteeTypes = [...new Set(advancedFilters.reporteeTypes)].map(type => {
          if (type === 'ONSHORE') return 'ON-SHORE';
          if (type === 'OFFSHORE') return 'OFF-SHORE';
          return type;
        });
      }

      // Check Types (array)
      if (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) {
        exportParams.checkTypes = [...new Set(advancedFilters.checkTypes)];
      }
      if (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) {
        exportParams.excludeCheckTypes = [...new Set(advancedFilters.excludeCheckTypes)];
      }

      // Workflow Statuses (array)
      if (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) {
        exportParams.workflowStatuses = [...new Set(advancedFilters.workflowStatuses)];
      }
      if (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) {
        exportParams.excludeWorkflowStatuses = [...new Set(advancedFilters.excludeWorkflowStatuses)];
      }

      // Unknown filter
      if (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) {
        exportParams.unknown = advancedFilters.unknown;
      }

      // Check number (supports wildcards)
      if (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') {
        exportParams.checkNumber = advancedFilters.checkNumber.trim().toUpperCase();
      }

      // Clarification type (supports wildcards)
      if (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') {
        exportParams.clarificationType = advancedFilters.clarificationType.trim();
      }
      if (advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0) {
        exportParams.excludeClarificationTypes = [...new Set(advancedFilters.excludeClarificationTypes)];
      }

      // Date filters - check if user has set any date filters
      const hasDepositDateFilter = advancedFilters.depositDateFrom || advancedFilters.depositDateTo;
      const hasResolvedDateFilter = advancedFilters.resolvedDateFrom || advancedFilters.resolvedDateTo;
      const hasOpenedDateFilter = advancedFilters.openedDateFrom || advancedFilters.openedDateTo;
      
      // Check if any advanced filters are active
      const isAdvancedFiltersActive = (
        (advancedFilters.statuses && advancedFilters.statuses.length > 0) ||
        (advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0) ||
        (advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0) ||
        (advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0) ||
        (advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0) ||
        (advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0) ||
        (advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0) ||
        (advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0) ||
        (advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0) ||
        (advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0) ||
        (advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0) ||
        (advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0) ||
        (advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0) ||
        (advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0) ||
        (advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0) ||
        (advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0) ||
        (advancedFilters.checkNumber && advancedFilters.checkNumber.trim() !== '') ||
        (advancedFilters.clarificationType && advancedFilters.clarificationType.trim() !== '') ||
        (advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0) ||
        (advancedFilters.unknown !== undefined && advancedFilters.unknown !== null) ||
        hasDepositDateFilter ||
        hasResolvedDateFilter ||
        hasOpenedDateFilter
      );
      
      // Get month range for default opened date (only if no advanced filters are active)
      const monthRange = getMonthRange(selectedMonth, selectedYear);
      
      // Deposit Date filters - only add if user explicitly set them
      if (advancedFilters.depositDateFrom && advancedFilters.depositDateFrom.trim() !== '') {
        exportParams.depositDateFrom = advancedFilters.depositDateFrom;
      }
      if (advancedFilters.depositDateTo && advancedFilters.depositDateTo.trim() !== '') {
        exportParams.depositDateTo = advancedFilters.depositDateTo;
      }

      // Resolved Date filters - only add if user explicitly set them
      if (advancedFilters.resolvedDateFrom && advancedFilters.resolvedDateFrom.trim() !== '') {
        exportParams.resolvedDateFrom = advancedFilters.resolvedDateFrom;
      }
      if (advancedFilters.resolvedDateTo && advancedFilters.resolvedDateTo.trim() !== '') {
        exportParams.resolvedDateTo = advancedFilters.resolvedDateTo;
      }

      // Opened Date filters
      // Only add default opened date if no advanced filters are active
      if (hasOpenedDateFilter) {
        if (advancedFilters.openedDateFrom) {
          exportParams.openedDateFrom = advancedFilters.openedDateFrom;
        }
        if (advancedFilters.openedDateTo) {
          exportParams.openedDateTo = advancedFilters.openedDateTo;
        }
      } else if (!isAdvancedFiltersActive) {
        // Only use default month range if NO advanced filters are active
        exportParams.openedDateFrom = monthRange.startDate;
        exportParams.openedDateTo = monthRange.endDate;
      }

      // Legacy date filters mapping
      if (advancedFilters.startDate && !exportParams.openedDateFrom) {
        exportParams.openedDateFrom = advancedFilters.startDate;
      }
      if (advancedFilters.endDate && !exportParams.openedDateTo) {
        exportParams.openedDateTo = advancedFilters.endDate;
      }
      
      // Call export API
      const blob = await api.exportClarificationReport(exportParams);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date
      const timestamp = new Date().toISOString().slice(0, 10);
      link.download = `clarification-report-${timestamp}.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error('Error exporting clarification report:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to export clarification report';
      showError(errorMessage);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyClarificationId = async (clarificationId) => {
    if (!clarificationId) return;
    try {
      await navigator.clipboard.writeText(clarificationId);
      showSuccess('Clarification ID copied');
    } catch (err) {
      console.error('Copy failed:', err);
      showError('Could not copy clarification ID');
    }
  };

  const SortArrow = ({ field }) => {
    if (sortField !== field) return null;
    return (
      <span className="sort-arrow">
        {sortDirection === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  return (
    <div className="clarifications-page">
      <div className="page-header">
        <div className="page-header-content">
          <h1 className="page-title">Clarifications</h1>
          <p className="page-subtitle">Manage and track payment clarifications</p>
        </div>
        <div className="header-actions">
          {!hasAdvancedFilters() && (
            <div className="date-range-container">
              <button 
                className="date-range-btn"
                onClick={() => setShowMonthPicker(!showMonthPicker)}
              >
                {getDateRangeDisplay()}
                <svg width="16" height="16" viewBox="0 0 20 20" fill="none" style={{ marginLeft: '8px' }}>
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {showMonthPicker && (
                <MonthYearPicker
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  onSelect={handleMonthYearSelect}
                  onClose={() => setShowMonthPicker(false)}
                />
              )}
            </div>
          )}
          {hasAdvancedFilters() && (
            <div className="date-range-container">
              <div className="date-range-display">
                {getDateRangeDisplay()}
              </div>
            </div>
          )}
          <button 
            onClick={handleExport}
            disabled={isExporting}
            className="btn-export"
            title="Export to Excel"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-bar">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="2"/>
            <path d="M15 15L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search by check number (e.g., ch* or ch), type, details..."
            value={searchTerm}
            onChange={(e) => {
              const filteredValue = filterEmojis(e.target.value);
              setSearchTerm(filteredValue);
            }}
          />
        </div>
        <div className="filter-dropdowns">
          <SearchableDropdown
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'OPEN', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' }
            ]}
            value={selectedStatus}
            onChange={(value) => setSelectedStatus(value)}
            placeholder="All Statuses"
            maxVisibleItems={5}
          />
          <button 
            className={`btn-advanced-filter ${hasAdvancedFilters() ? 'active' : ''}`}
            onClick={() => setShowAdvancedFilters(true)}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M3 5H17M7 10H17M11 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Advanced Filters
          </button>
        </div>
      </div>

      {/* Active Filters Indicator */}
      {(hasAdvancedFilters() || selectedStatus || searchTerm) && (
        <div className="active-filters-section">
          <div className="active-filters-label">Active Filters:</div>
          <div className="active-filters-chips">
            {searchTerm && (
              <div className="filter-chip">
                <span>Search: {searchTerm}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSearchTerm('')}
                  title="Remove search filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {selectedStatus && (
              <div className="filter-chip">
                <span>Status: {[
                  { value: 'OPEN', label: 'Open' },
                  { value: 'RESOLVED', label: 'Resolved' }
                ].find(s => s.value === selectedStatus)?.label || selectedStatus}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => setSelectedStatus('')}
                  title="Remove status filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {/* Multiple Status Filters */}
            {advancedFilters.statuses && advancedFilters.statuses.length > 0 && advancedFilters.statuses.map((status) => {
              const statusLabels = {
                'OPEN': 'Open',
                'RESOLVED': 'Resolved'
              };
              return (
                <div key={status} className="filter-chip">
                  <span>Status: {statusLabels[status] || status}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        statuses: prev.statuses.filter(s => s !== status)
                      }));
                    }}
                    title="Remove status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeStatuses && advancedFilters.excludeStatuses.length > 0 && advancedFilters.excludeStatuses.map((status) => {
              const statusLabels = { OPEN: 'Open', RESOLVED: 'Resolved' };
              return (
                <div key={`exclude-${status}`} className="filter-chip filter-chip--exclude">
                  <span>Status: Not {statusLabels[status] || status}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeStatuses: prev.excludeStatuses.filter(s => s !== status) }))} title="Remove excluded status filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Practice Filters */}
            {advancedFilters.practiceCodes && advancedFilters.practiceCodes.length > 0 && advancedFilters.practiceCodes.map((practiceCode) => (
              <div key={practiceCode} className="filter-chip">
                <span>Practice: {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      practiceCodes: prev.practiceCodes.filter(p => p !== practiceCode),
                      locationCodes: prev.locationCodes.filter(loc => {
                        // Remove locations that belong to this practice
                        const practice = practices.find(p => p.code === practiceCode);
                        if (practice && practice.practiceId) {
                          // This would need location data to check, for now just remove all
                          return false;
                        }
                        return true;
                      })
                    }));
                  }}
                  title="Remove practice filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludePracticeCodes && advancedFilters.excludePracticeCodes.length > 0 && advancedFilters.excludePracticeCodes.map((practiceCode) => (
              <div key={`exclude-${practiceCode}`} className="filter-chip filter-chip--exclude">
                <span>Practice: Not {practices.find(p => p.code === practiceCode)?.name || practiceCode}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludePracticeCodes: prev.excludePracticeCodes.filter(p => p !== practiceCode) }))} title="Remove excluded practice filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Location Filters */}
            {advancedFilters.locationCodes && advancedFilters.locationCodes.length > 0 && advancedFilters.locationCodes.map((locationCode) => (
              <div key={locationCode} className="filter-chip">
                <span>Location: {locationCode}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      locationCodes: prev.locationCodes.filter(l => l !== locationCode)
                    }));
                  }}
                  title="Remove location filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeLocationCodes && advancedFilters.excludeLocationCodes.length > 0 && advancedFilters.excludeLocationCodes.map((locationCode) => (
              <div key={`exclude-${locationCode}`} className="filter-chip filter-chip--exclude">
                <span>Location: Not {locationCode}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeLocationCodes: prev.excludeLocationCodes.filter(l => l !== locationCode) }))} title="Remove excluded location filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Filters */}
            {advancedFilters.assigneeIds && advancedFilters.assigneeIds.length > 0 && advancedFilters.assigneeIds.map((assigneeId) => (
              <div key={assigneeId} className="filter-chip">
                <span>Assignee: {getUserName(assigneeId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      assigneeIds: prev.assigneeIds.filter(id => id !== assigneeId)
                    }));
                  }}
                  title="Remove assignee filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeAssigneeIds && advancedFilters.excludeAssigneeIds.length > 0 && advancedFilters.excludeAssigneeIds.map((assigneeId) => (
              <div key={`exclude-assignee-${assigneeId}`} className="filter-chip filter-chip--exclude">
                <span>Assignee: Not {getUserName(assigneeId)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeAssigneeIds: prev.excludeAssigneeIds.filter(id => id !== assigneeId) }))} title="Remove excluded assignee filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Reporter Filters */}
            {advancedFilters.reporterIds && advancedFilters.reporterIds.length > 0 && advancedFilters.reporterIds.map((reporterId) => (
              <div key={reporterId} className="filter-chip">
                <span>Reporter: {getUserName(reporterId)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      reporterIds: prev.reporterIds.filter(id => id !== reporterId)
                    }));
                  }}
                  title="Remove reporter filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            ))}
            {advancedFilters.excludeReporterIds && advancedFilters.excludeReporterIds.length > 0 && advancedFilters.excludeReporterIds.map((reporterId) => (
              <div key={`exclude-reporter-${reporterId}`} className="filter-chip filter-chip--exclude">
                <span>Reporter: Not {getUserName(reporterId)}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeReporterIds: prev.excludeReporterIds.filter(id => id !== reporterId) }))} title="Remove excluded reporter filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {/* Multiple Assignee Type Filters */}
            {advancedFilters.assigneeTypes && advancedFilters.assigneeTypes.length > 0 && advancedFilters.assigneeTypes.map((assigneeType) => {
              const assigneeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={assigneeType} className="filter-chip">
                  <span>Assignee: {assigneeTypeLabels[assigneeType] || assigneeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        assigneeTypes: prev.assigneeTypes.filter(at => at !== assigneeType)
                      }));
                    }}
                    title="Remove assignee type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Reportee Type Filters */}
            {advancedFilters.reporteeTypes && advancedFilters.reporteeTypes.length > 0 && advancedFilters.reporteeTypes.map((reporteeType) => {
              const reporteeTypeLabels = {
                'ONSHORE': 'Onshore',
                'OFFSHORE': 'Offshore'
              };
              return (
                <div key={reporteeType} className="filter-chip">
                  <span>Reporter: {reporteeTypeLabels[reporteeType] || reporteeType}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        reporteeTypes: prev.reporteeTypes.filter(rt => rt !== reporteeType)
                      }));
                    }}
                    title="Remove reportee type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Check Type Filters */}
            {advancedFilters.checkTypes && advancedFilters.checkTypes.length > 0 && advancedFilters.checkTypes.map((checkType) => {
              const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
              const label = checkTypeItem?.name || checkType;
              return (
                <div key={checkType} className="filter-chip">
                  <span>Check Type: {label}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        checkTypes: prev.checkTypes.filter(ct => ct !== checkType)
                      }));
                    }}
                    title="Remove check type filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeCheckTypes && advancedFilters.excludeCheckTypes.length > 0 && advancedFilters.excludeCheckTypes.map((checkType) => {
              const checkTypeItem = checkTypes.find(ct => ct.code === checkType);
              const label = checkTypeItem?.name || checkType;
              return (
                <div key={`exclude-${checkType}`} className="filter-chip filter-chip--exclude">
                  <span>Check Type: Not {label}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeCheckTypes: prev.excludeCheckTypes.filter(ct => ct !== checkType) }))} title="Remove excluded check type filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {/* Multiple Workflow Status Filters */}
            {advancedFilters.workflowStatuses && advancedFilters.workflowStatuses.length > 0 && advancedFilters.workflowStatuses.map((workflowStatus) => {
              const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
              const label = workflowStatusItem?.name || workflowStatus;
              return (
                <div key={workflowStatus} className="filter-chip">
                  <span>Workflow Status: {label}</span>
                  <button
                    className="chip-close-btn"
                    onClick={() => {
                      setAdvancedFilters(prev => ({
                        ...prev,
                        workflowStatuses: prev.workflowStatuses.filter(ws => ws !== workflowStatus)
                      }));
                    }}
                    title="Remove workflow status filter"
                  >
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                      <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.excludeWorkflowStatuses && advancedFilters.excludeWorkflowStatuses.length > 0 && advancedFilters.excludeWorkflowStatuses.map((workflowStatus) => {
              const workflowStatusItem = workflowStatuses.find(ws => ws.code === workflowStatus);
              const label = workflowStatusItem?.name || workflowStatus;
              return (
                <div key={`exclude-${workflowStatus}`} className="filter-chip filter-chip--exclude">
                  <span>Workflow Status: Not {label}</span>
                  <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeWorkflowStatuses: prev.excludeWorkflowStatuses.filter(ws => ws !== workflowStatus) }))} title="Remove excluded workflow status filter">
                    <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              );
            })}
            {advancedFilters.checkNumber && (
              <div className="filter-chip">
                <span>Check: {advancedFilters.checkNumber}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, checkNumber: '' }));
                  }}
                  title="Remove check number filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.clarificationType && (
              <div className="filter-chip">
                <span>Clarification Type: {advancedFilters.clarificationType}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, clarificationType: '' }));
                  }}
                  title="Remove clarification type filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {advancedFilters.excludeClarificationTypes && advancedFilters.excludeClarificationTypes.length > 0 && advancedFilters.excludeClarificationTypes.map((clarificationType) => (
              <div key={`exclude-${clarificationType}`} className="filter-chip filter-chip--exclude">
                <span>Clarification Type: Not {clarificationType}</span>
                <button className="chip-close-btn" onClick={() => setAdvancedFilters(prev => ({ ...prev, excludeClarificationTypes: prev.excludeClarificationTypes.filter(ct => ct !== clarificationType) }))} title="Remove excluded clarification type filter">
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </button>
              </div>
            ))}
            {advancedFilters.unknown !== undefined && advancedFilters.unknown !== null && (
              <div className="filter-chip">
                <span>Unknown: {advancedFilters.unknown === true ? 'True' : 'False'}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, unknown: undefined }));
                  }}
                  title="Remove unknown filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.depositDateFrom || advancedFilters.depositDateTo) && (
              <div className="filter-chip">
                <span>Deposit Date: {formatDateRange(
                  advancedFilters.depositDateFrom || '',
                  advancedFilters.depositDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      depositDateFrom: '',
                      depositDateTo: ''
                    }));
                  }}
                  title="Remove deposit date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.resolvedDateFrom || advancedFilters.resolvedDateTo) && (
              <div className="filter-chip">
                <span>Resolved Date: {formatDateRange(
                  advancedFilters.resolvedDateFrom || '',
                  advancedFilters.resolvedDateTo || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      resolvedDateFrom: '',
                      resolvedDateTo: ''
                    }));
                  }}
                  title="Remove resolved date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.openedDateFrom || advancedFilters.openedDateTo || advancedFilters.startDate || advancedFilters.endDate) && (
              <div className="filter-chip">
                <span>Opened Date: {formatDateRange(
                  advancedFilters.openedDateFrom || advancedFilters.startDate || '',
                  advancedFilters.openedDateTo || advancedFilters.endDate || ''
                )}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({
                      ...prev,
                      openedDateFrom: '',
                      openedDateTo: '',
                      startDate: '',
                      endDate: ''
                    }));
                  }}
                  title="Remove date range filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            {(advancedFilters.month || advancedFilters.year) && (
              <div className="filter-chip">
                <span>Period: {formatMonthYear(advancedFilters.month || selectedMonth, advancedFilters.year || selectedYear)}</span>
                <button
                  className="chip-close-btn"
                  onClick={() => {
                    setAdvancedFilters(prev => ({ ...prev, month: null, year: null }));
                  }}
                  title="Remove period filter"
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="none">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            <button
              className="clear-all-filters-btn"
              onClick={handleClearAllFilters}
              title="Clear all filters"
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="table-section">
        <div className="table-header">
          <div className="table-header-left">
            {totalElements > 0 && showPagination && (
              <div className="pagination-info">
                <button
                  className="pagination-arrow"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 0 || loading}
                  title="Previous page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <span className="pagination-text">
                  {startIndex} - {endIndex}, of {displayTotal}
                </span>
                {totalPages > 1 && (
                  <div className="pagination-page-input">
                    <span style={{ fontSize: '12px', color: '#6b7280', marginRight: '4px' }}>Page:</span>
                    <input
                      type="text"
                      value={pageInputValue}
                      onChange={handlePageInputChange}
                      onKeyPress={handlePageInputKeyPress}
                      onBlur={handleGoToPage}
                      style={{
                        width: '45px',
                        padding: '2px 4px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '4px',
                        fontSize: '12px',
                        textAlign: 'center',
                        height: '24px'
                      }}
                    />
                    <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>
                      of {totalPages}
                    </span>
                  </div>
                )}
                <button
                  className="pagination-arrow"
                  onClick={handleNextPage}
                  disabled={!hasMore || loading}
                  title="Next page"
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
          <div className="table-actions">
            <Tooltip text="Reset Column widths" position="top">
              <button
                type="button"
                className="icon-btn"
                onClick={resetColumnWidths}
                aria-label="Reset Column widths"
              >
                <RotateCcw size={18} strokeWidth={2} />
              </button>
            </Tooltip>
            <button 
              ref={columnSelectorTriggerRef}
              className="icon-btn"
              onClick={() => setShowColumnSelector(!showColumnSelector)}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 5H17M3 10H17M3 15H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="clarifications-table clarifications-table--col-resize">
            <Colgroup />
            <thead>
              <tr>
                {visibleColumns.map(colKey => {
                  const column = allColumns.find(col => col.key === colKey);
                  if (!column) return null;

                  switch (colKey) {
                    case 'checkNumber':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('checkNumber')}
                        >
                          Check Number <SortArrow field="checkNumber" />
                        </ResizableTh>
                      );
                    case 'clarificationId':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('clarificationId')}
                        >
                          Clarification ID <SortArrow field="clarificationId" />
                        </ResizableTh>
                      );
                    case 'clarificationType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('clarificationType')}
                        >
                          Clarification Type <SortArrow field="clarificationType" />
                        </ResizableTh>
                      );
                    case 'checkType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('checkType')}
                        >
                          Check Type <SortArrow field="checkType" />
                        </ResizableTh>
                      );
                    case 'depositDate':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('depositDate')}
                        >
                          Deposit Date <SortArrow field="depositDate" />
                        </ResizableTh>
                      );
                    case 'amount':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('amount')}
                        >
                          Amount <SortArrow field="amount" />
                        </ResizableTh>
                      );
                    case 'pageNumber':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Page Number
                        </ResizableTh>
                      );
                    case 'patientName':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Patient Name
                        </ResizableTh>
                      );
                    case 'patientAccountNumber':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Patient Account #
                        </ResizableTh>
                      );
                    case 'insurance':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Insurance
                        </ResizableTh>
                      );
                    case 'details':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Details
                        </ResizableTh>
                      );
                    case 'assigneeType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('assignee')}
                        >
                          Assignee <SortArrow field="assignee" />
                        </ResizableTh>
                      );
                    case 'assignee':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Assignee Name
                        </ResizableTh>
                      );
                    case 'reporteeType':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('reportee')}
                        >
                          Reporter <SortArrow field="reportee" />
                        </ResizableTh>
                      );
                    case 'reporter':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Reporter Name
                        </ResizableTh>
                      );
                    case 'status':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('status')}
                        >
                          Status <SortArrow field="status" />
                        </ResizableTh>
                      );
                    case 'workflowStatus':
                      return (
                        <ResizableTh key={colKey} columnId={colKey} onResizeMouseDown={onResizeMouseDown}>
                          Workflow Status
                        </ResizableTh>
                      );
                    case 'openedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('openedAt')}
                        >
                          Opened Date <SortArrow field="openedAt" />
                        </ResizableTh>
                      );
                    case 'resolvedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('resolvedAt')}
                        >
                          Resolved Date <SortArrow field="resolvedAt" />
                        </ResizableTh>
                      );
                    case 'createdAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('createdAt')}
                        >
                          Created At <SortArrow field="createdAt" />
                        </ResizableTh>
                      );
                    case 'updatedAt':
                      return (
                        <ResizableTh
                          key={colKey}
                          columnId={colKey}
                          onResizeMouseDown={onResizeMouseDown}
                          className="sortable"
                          onClick={() => handleSort('updatedAt')}
                        >
                          Updated At <SortArrow field="updatedAt" />
                        </ResizableTh>
                      );
                    default:
                      return null;
                  }
                })}
              </tr>
            </thead>
            <tbody>
              {loading && clarifications.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    Loading clarifications...
                  </td>
                </tr>
              ) : clarifications.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length} style={{ textAlign: 'center', padding: '40px' }}>
                    No clarifications found
                  </td>
                </tr>
              ) : (
                clarifications.map((clarification) => (
                  <tr
                    key={clarification.clarificationId}
                    className="clarifications-table-row--clickable"
                    onClick={() => openClarificationDetail(clarification)}
                  >
                    {visibleColumns.map(colKey => {
                      switch (colKey) {
                        case 'checkNumber':
                          return (
                            <td key={colKey} className="clarification-id-table-cell">
                              {clarification.checkNumber ? (
                                <div className="clarification-id-cell-inner">
                                  <button
                                    type="button"
                                    className="clarifications-id-copy-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyCheckNumber(clarification.checkNumber);
                                    }}
                                    title="Copy check number"
                                    aria-label="Copy check number"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <CellEllipsis text={clarification.checkNumber} />
                                  </div>
                                </div>
                              ) : (
                                <CellEllipsis text="N/A" />
                              )}
                            </td>
                          );
                        case 'clarificationId':
                          return (
                            <td key={colKey} className="clarification-id-table-cell">
                              {clarification.clarificationId ? (
                                <div className="clarification-id-cell-inner">
                                  <button
                                    type="button"
                                    className="clarifications-id-copy-btn"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopyClarificationId(clarification.clarificationId);
                                    }}
                                    title="Copy clarification ID"
                                    aria-label="Copy clarification ID"
                                  >
                                    <Copy size={16} aria-hidden />
                                  </button>
                                  <div style={{ minWidth: 0, flex: 1 }}>
                                    <CellEllipsis text={clarification.clarificationId} />
                                  </div>
                                </div>
                              ) : (
                                'N/A'
                              )}
                            </td>
                          );
                        case 'clarificationType':
                          return (
                            <td key={colKey}>
                              {(() => {
                                if (!clarification.clarificationType) return 'N/A';
                                const clarTypeItem = clarificationTypes.find(ct => ct.code === clarification.clarificationType);
                                const displayName = clarTypeItem?.name || clarification.clarificationType;
                                return <CellEllipsis text={displayName} />;
                              })()}
                            </td>
                          );
                        case 'checkType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis
                                text={
                                  (() => {
                                    if (!clarification.checkType) return 'N/A';
                                    const checkTypeItem = checkTypes.find(ct => ct.code === clarification.checkType);
                                    return checkTypeItem?.name || clarification.checkType;
                                  })()
                                }
                              />
                            </td>
                          );
                        case 'depositDate':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.depositDate ? formatDateUS(clarification.depositDate) : 'N/A'} />
                            </td>
                          );
                        case 'amount':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={formatCurrency(clarification.amount)} />
                            </td>
                          );
                        case 'pageNumber':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={String(clarification.pageNumber ?? '-')} />
                            </td>
                          );
                        case 'patientName':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.patientName || '-'} />
                            </td>
                          );
                        case 'patientAccountNumber':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.patientAccountNumber || '-'} />
                            </td>
                          );
                        case 'insurance':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.insurance || '-'} />
                            </td>
                          );
                        case 'details':
                          return (
                            <td key={colKey} className="details-cell">
                              <CellEllipsis text={clarification.details || ''} />
                            </td>
                          );
                        case 'assignee':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(clarification.assigneeId)} />
                            </td>
                          );
                        case 'assigneeType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.assignee || 'N/A'} />
                            </td>
                          );
                        case 'reporter':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={getUserName(clarification.reporterId)} />
                            </td>
                          );
                        case 'reporteeType':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.reportee || 'N/A'} />
                            </td>
                          );
                        case 'status':
                          return (
                            <td key={colKey}>
                              <span className={`status-badge ${getStatusClass(clarification.status)}`}>
                                <CellEllipsis text={formatStatus(clarification.status)} />
                              </span>
                            </td>
                          );
                        case 'workflowStatus':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.workflowStatus || 'N/A'} />
                            </td>
                          );
                        case 'openedAt':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.openedAt ? formatDateTime(clarification.openedAt) : 'N/A'} />
                            </td>
                          );
                        case 'resolvedAt':
                          return (
                            <td key={colKey}>
                              <CellEllipsis text={clarification.resolvedAt ? formatDateTime(clarification.resolvedAt) : 'N/A'} />
                            </td>
                          );
                        case 'createdAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={clarification.createdBy}
                                dateTime={clarification.createdAt}
                                action="created"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        case 'updatedAt':
                          return (
                            <td key={colKey} className="td-timestamp-cell">
                              <UserTimestamp
                                userId={clarification.updatedBy}
                                dateTime={clarification.updatedAt}
                                action="updated"
                                getUserName={getUserName}
                                getUserById={getUserById}
                              />
                            </td>
                          );
                        default:
                          return null;
                      }
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <AdvancedFilterDrawer
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        filters={advancedFilters}
        onApplyFilters={handleApplyAdvancedFilters}
        onResetFilters={handleResetAdvancedFilters}
        isClarifications={true}
        filterType="CLARIFICATION_DASHBOARD"
      />

      {showColumnSelector && (
        <ColumnSelector
          availableColumns={allColumns}
          visibleColumns={visibleColumns}
          defaultColumns={defaultColumns}
          onChange={(cols) => {
            setVisibleColumns(cols);
            persistVisibleColumns(CLARIFICATIONS_COLUMN_PREFS_KEY, cols);
          }}
          onClose={() => setShowColumnSelector(false)}
          triggerRef={columnSelectorTriggerRef}
        />
      )}
    </div>
  );
};

export default Clarifications;
